import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import {
    BudgetProposalResourceMetadataT,
    BudgetProposalT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import { MANTY_CYAN } from 'styles/theme';
import { trackEventBudget } from 'helpers/trackerHelpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { BudgetExpenseOrIncome } from 'business/constants';
import { getInscriptionInformation } from '../dataHelpers';
import MoveInscription from '../MoveInscription/MoveInscription';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

const StyBox = styled(Box, {
    name: 'StyBox',
})({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 'initial',
});

const StyButton = styled(Button, {
    name: 'StyButton',
})({
    backgroundColor: 'rgba(3, 110, 187, 0.15)',
    color: MANTY_CYAN,
    border: `1px solid ${MANTY_CYAN}`,
    fontSize: '12px',
    paddingTop: '2px',
    paddingBottom: '2px',
    minHeight: '0',
    '& .MuiButton-label': {
        fontWeight: '400',
    },
});

type ClassifyButtonCellRendererParams =
    CellRendererParams<InscriptionWithMetadata> & {
        expenseOrIncome: BudgetExpenseOrIncome;
        budgetProposals: BudgetProposalT[];
        inscriptionIdColumn?: keyof InscriptionT;
        queryCacheKey: InscriptionQueryKey;
        trackingInformation?: {
            eventName: string;
            metadata?: BudgetProposalResourceMetadataT;
        };
    };

export default function ClassifyButtonCellRenderer({
    data,
    expenseOrIncome,
    budgetProposals,
    inscriptionIdColumn,
    queryCacheKey,
    api,
    trackingInformation,
}: ClassifyButtonCellRendererParams): React.ReactElement {
    const { t } = useTranslation();

    const [showAssignmentModal, setShowAssignmentModal] = useState(false);
    const [selectedInscription, setSelectedInscription] =
        useState<InscriptionT | null>(null);
    const statusesSettings = useContext(StatusSettingsContext);
    const { inscription, budgetProposal } = getInscriptionInformation(
        data,
        budgetProposals,
        statusesSettings,
        inscriptionIdColumn,
    );
    if (!data || budgetProposal === undefined) {
        return <span />;
    }
    const showMoveModal = (ins: InscriptionT) => {
        setSelectedInscription(ins);
        setShowAssignmentModal(true);
    };
    const onCloseModal = () => {
        setShowAssignmentModal(false);
        if (trackingInformation)
            trackEventBudget(
                trackingInformation.eventName,
                trackingInformation.metadata,
            );
    };
    const inscriptions: InscriptionWithMetadata[] = [];
    api.forEachNode((node: { data: InscriptionWithMetadata | undefined }) => {
        // Some node.data are undefined when we are doing some row grouping
        // The undefined node.data represent aggregation nodes
        if (node.data) inscriptions.push(node.data);
    });

    return (
        <StyBox>
            <StyButton
                disabled={!data.inscription.canMove}
                onClick={() => {
                    // TODO StrictNullCheck : Try to remove "!"
                    showMoveModal(inscription!);
                }}
                variant="text"
            >
                {t(`budgetProposal.classify`)}
            </StyButton>
            {showAssignmentModal && selectedInscription && (
                <MoveInscription
                    isOpen={showAssignmentModal}
                    onClose={onCloseModal}
                    budgetProposal={budgetProposal}
                    // TODO StrictNullCheck : Try to remove "!"
                    inscriptionIdColumn={inscriptionIdColumn!}
                    inscriptions={inscriptions}
                    queryCacheKey={queryCacheKey}
                    selectedInscription={selectedInscription}
                    budgetProposals={budgetProposals}
                    expenseOrIncome={expenseOrIncome}
                />
            )}
        </StyBox>
    );
}
